import { PageHeader } from "@/components/PageHeader";
import { Badge, statusColor, type BadgeColor } from "@/components/DataTable";
import { CheckCircle2, XCircle, Clock, Hourglass, Ban, AlertCircle } from "lucide-react";

type StatusInfo = {
  status: string;
  badge: BadgeColor;
  icon: React.ReactNode;
  short: string;
  description: string;
  next: string[];
};

const STATUSES: StatusInfo[] = [
  {
    status: "pending",
    badge: "warning",
    icon: <Clock size={16} />,
    short: "Tentative créée, en attente de l'utilisateur final",
    description:
      "Le paiement vient d'être initié. Le client doit valider sur son téléphone (code PIN mobile money) ou compléter la 3DS sur la page bancaire.",
    next: ["processing", "succeeded", "failed", "timeout"],
  },
  {
    status: "processing",
    badge: "warning",
    icon: <Hourglass size={16} />,
    short: "En cours de traitement par le switch",
    description:
      "Le switch (telco ou Cybersource) a accepté la demande et l'achemine. Aucune action n'est requise — le résultat final arrivera via callback.",
    next: ["succeeded", "failed", "timeout"],
  },
  {
    status: "succeeded",
    badge: "success",
    icon: <CheckCircle2 size={16} />,
    short: "Paiement réussi — fonds débités du client",
    description:
      "Le client a été débité avec succès. Les fonds sont crédités au wallet Moko du marchand (net de la commission). Un webhook payment.succeeded est envoyé au VRS.",
    next: [],
  },
  {
    status: "failed",
    badge: "danger",
    icon: <XCircle size={16} />,
    short: "Échec — client non débité",
    description:
      "Le switch a renvoyé un échec définitif. Aucun débit côté client. La cause précise est disponible dans le champ description (ex: solde insuffisant, PIN incorrect, carte refusée).",
    next: [],
  },
  {
    status: "cancelled",
    badge: "danger",
    icon: <Ban size={16} />,
    short: "Annulé par le client",
    description:
      "Le client a explicitement annulé la transaction (ferme la page de paiement, annule sur son téléphone). Aucun débit.",
    next: [],
  },
  {
    status: "timeout",
    badge: "danger",
    icon: <AlertCircle size={16} />,
    short: "Délai de validation dépassé",
    description:
      "Le client n'a pas validé dans le délai imparti (typiquement 3 minutes). La transaction est expirée côté switch. Aucun débit.",
    next: [],
  },
  {
    status: "expired",
    badge: "light",
    icon: <Clock size={16} />,
    short: "Session de checkout expirée avant initiation",
    description:
      "La session de paiement hosted checkout a expiré avant que le client choisisse un moyen de paiement (URL signée TTL dépassé).",
    next: [],
  },
];

export function TransactionStatusesPage() {
  return (
    <div>
      <PageHeader
        title="Statuts des transactions"
        breadcrumbs={[{ label: "Transactions" }, { label: "Statuts" }]}
      />

      <div className="card mb-6">
        <div className="card-body">
          <div className="text-sm text-[#3f4254] leading-relaxed">
            Chaque paiement traverse plusieurs statuts au cours de son cycle de vie. Voici la liste
            complète des statuts possibles avec leur signification et les transitions autorisées.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {STATUSES.map((s) => (
          <div key={s.status} className="card">
            <div className="card-body">
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg(s.badge)}`}>
                  {s.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge color={s.badge}>{s.status}</Badge>
                  </div>
                  <div className="font-semibold text-[#181c32]">{s.short}</div>
                </div>
              </div>
              <div className="text-sm text-[#3f4254] leading-relaxed">{s.description}</div>
              {s.next.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#eff2f5]">
                  <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold mb-2">
                    Transitions possibles
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.next.map((n) => (
                      <Badge key={n} color={statusColor(n)}>
                        → {n}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="card mt-6">
        <div className="card-header">
          <div className="card-title">Statuts terminaux vs intermédiaires</div>
        </div>
        <div className="card-body space-y-3 text-sm text-[#3f4254] leading-relaxed">
          <p>
            <strong>Statuts terminaux</strong> (transitions vers d'autres statuts impossibles) :{" "}
            <Badge color="success">succeeded</Badge> <Badge color="danger">failed</Badge>{" "}
            <Badge color="danger">cancelled</Badge> <Badge color="danger">timeout</Badge>{" "}
            <Badge color="light">expired</Badge>
          </p>
          <p>
            <strong>Statuts intermédiaires</strong> : <Badge color="warning">pending</Badge>{" "}
            <Badge color="warning">processing</Badge> — un paiement n'y reste pas indéfiniment, il
            transitionne automatiquement vers un statut terminal au plus tard après <strong>3 minutes</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

function iconBg(c: BadgeColor): string {
  return {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    danger: "bg-danger-50 text-danger-700",
    warning: "bg-warning-50 text-warning-700",
    info: "bg-info-50 text-info-700",
    light: "bg-[#f5f8fa] text-[#5e6278]",
  }[c];
}
